import {
  z,
  ZodTypeAny,
  ZodOptional,
  ZodNullable,
  ZodDefault,
  ZodEffects,
  ZodObject,
  ZodArray,
  ZodUnion,
  ZodLazy,
  ZodType,
} from 'zod'

export const ZOD_META = Symbol('zod.meta')

export type ZodMeta = {
  [key: string]: unknown
}

/**
 * Métodos adicionais que queremos injetar no schema.
 */
export interface ZodTypeWithMeta {
  meta(metadata: ZodMeta): this
  getMeta(): ZodMeta
}

/**
 * Tipo para envio de shape via API
 */
export type ZodSchemaInfo = {
  type: string
  meta: ZodMeta
  label?: string
  shape?: ZodSchemaInfo | Record<string, ZodSchemaInfo> | ZodSchemaInfo[]
}

/**
 * Função para obter metadados de um schema Zod estendido com withMeta
 * @param schema Schema Zod que pode conter metadados
 * @returns Objeto com os metadados ou um objeto vazio se não houver metadados
 */
export function getMeta<T extends z.ZodTypeAny>(schema: T): ZodMeta {
  // Verifica se é um schema estendido com withMeta (tem o método getMeta)
  if (typeof (schema as any).getMeta === 'function') {
    return (schema as any).getMeta()
  }

  // Se não tiver o método getMeta, tenta acessar diretamente a propriedade
  return ((schema as any)._def?.[ZOD_META] as ZodMeta) || undefined
}

/**
 * Aqui fazemos uma assinatura que preserva os tipos de entrada/saída do Zod original.
 *
 * - z.output<T> é o tipo que o Zod produz ao fazer .parse()
 * - z.input<T> é o tipo que o Zod espera para validar
 * - T['_def'] é a definição interna (param do Zod)
 *
 * O retorno é "z.ZodType<z.output<T>, T['_def'], z.input<T>> & ZodTypeWithMeta",
 * garantindo que não vamos perder a inferência do T original.
 */
export function withMeta<T extends z.ZodTypeAny>(
  schema: T,
): z.ZodType<z.output<T>, T['_def'], z.input<T>> & ZodTypeWithMeta {
  // Fazemos o cast
  const extendedSchema = schema as unknown as z.ZodType<z.output<T>, T['_def'], z.input<T>> &
    ZodTypeWithMeta

  extendedSchema.meta = function (metadata: ZodMeta) {
    ;(this._def as any)[ZOD_META] = metadata
    return this
  }

  extendedSchema.getMeta = function () {
    return ((this._def as any)[ZOD_META] || {}) as ZodMeta
  }

  return extendedSchema
}

/**
 * Exemplo de wrappers para tipos primitivos:
 */
export const string = () => withMeta(z.string())
export const number = () => withMeta(z.number())
export const boolean = () => withMeta(z.boolean())
export const date = () => withMeta(z.date())
export const dateTime = () => withMeta(z.date())

/**
 * Transforma zod schema em schema info para envio via API substituindo o antigo shape de variable
 * @param schema zod schema
 * @returns schema info
 */
export function getZodSchemaInfo(schema: ZodTypeAny): ZodSchemaInfo {
  // Processa efeitos como transform, preprocess, etc.
  if (schema instanceof ZodEffects) {
    const subtypeSchema = (schema.innerType() as ZodTypeAny)?.describe(schema.description || '')
    return getZodSchemaInfo(subtypeSchema)
  }

  // Processa tipos que podem ser "desempacotados"
  if (schema instanceof ZodOptional || schema instanceof ZodNullable) {
    const subtypeSchema = (schema.unwrap() as ZodTypeAny)?.describe(schema.description || '')
    return getZodSchemaInfo(subtypeSchema)
  }

  if (schema instanceof ZodDefault) {
    const subtypeSchema = (schema.removeDefault() as ZodTypeAny)?.describe(schema.description || '')
    return getZodSchemaInfo(subtypeSchema)
  }

  const baseInfo: ZodSchemaInfo = {
    type: zodTypeToTsType(schema?._def?.typeName ?? ''),
    meta: getMeta(schema),
    label: schema.description,
  }

  if (schema instanceof ZodObject) {
    const shape = schema.shape
    const result: Record<string, ZodSchemaInfo> = {}

    Object.entries(shape).forEach(([key, value]) => {
      const info = getZodSchemaInfo(value as ZodTypeAny)
      result[key] = ('type' in info ? info : { type: 'object', shape: info }) as ZodSchemaInfo
    })

    baseInfo.shape = result
  }

  if (schema instanceof ZodArray) {
    const elementInfo = getZodSchemaInfo(schema.element)
    baseInfo.shape = ('type' in elementInfo ? elementInfo : []) as ZodSchemaInfo
  } else if (schema instanceof ZodUnion) {
    if (schema.options.length > 0) {
      const schemaUnion = (schema.options?.[0] as ZodTypeAny)?.describe(schema.description || '')
      return getZodSchemaInfo(schemaUnion)
    } else return baseInfo
  } else if (schema?._type === 'expression') {
    baseInfo.type = 'expression'
    if (baseInfo.meta) baseInfo.shape = getZodSchemaInfo(z.object({ ...(baseInfo.meta as any) }))
  } else if (schema instanceof ZodLazy) {
    baseInfo.shape = getZodSchemaInfo(schema.schema)
  }

  return baseInfo as ZodSchemaInfo
}

function zodTypeToTsType(zodTypeName: string): string {
  const typeMap: Record<string, string> = {
    ZodString: 'string',
    ZodNumber: 'number',
    ZodBoolean: 'boolean',
    ZodBigInt: 'bigint',
    ZodSymbol: 'symbol',
    ZodUndefined: 'undefined',
    ZodNull: 'null',
    ZodVoid: 'void',
    ZodAny: 'any',
    ZodUnknown: 'unknown',
    ZodNever: 'never',
    ZodDate: 'Date',
    ZodArray: 'Array',
    ZodObject: 'object',
    ZodRecord: 'Record<string, any>',
    ZodMap: 'Map',
    ZodSet: 'Set',
    ZodPromise: 'Promise',
    ZodLiteral: 'literal',
    ZodEnum: 'enumerated',
    ZodNativeEnum: 'enum',
    ZodLazy: 'lazy',
  }
  return typeMap[zodTypeName] || 'unknown'
}

export function getBaseType(schema: z.ZodTypeAny): z.ZodTypeAny {
  if (schema instanceof z.ZodOptional) {
    return getBaseType(schema.unwrap())
  }

  if (schema instanceof z.ZodNullable) {
    return getBaseType(schema._def.innerType)
  }

  if (schema instanceof z.ZodEffects) {
    return getBaseType(schema._def.schema)
  }

  if (schema instanceof z.ZodDefault) {
    return getBaseType(schema._def.innerType)
  }

  return schema
}
